import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getTeamUserRoleLabel } from "@/enums/team-user-role";
import { useTranslation } from "@/hooks/useTranslation";
import { getAvatarColor } from "@/lib/avatar-color";
import { convertDateToDistanceNow } from "@/lib/date";
import { getInitialsFromParts } from "@/lib/initials";
import { cn } from "@/lib/utils";
import { CollaboratorListingsDTO } from "@shaarp/common";
import {
  Building2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  InfoIcon,
  UsersIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

export default function CollaboratorsListingsList({
  collaborators,
  listingId,
}: {
  collaborators: CollaboratorListingsDTO[];
  listingId: string | undefined;
}) {
  const { t, currentLanguage } = useTranslation();
  const location = useLocation();

  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    const activeCollab = collaborators.find((c) =>
      c.listings.some((l) => l.id === listingId),
    );
    if (activeCollab) {
      initial.add(activeCollab.user.id);
    }
    return initial;
  });

  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState<
    string | null
  >(null);

  const effectiveSelectedCollaboratorId = useMemo(() => {
    if (!listingId) {
      return null;
    }
    if (selectedCollaboratorId) {
      const collab = collaborators.find(
        (c) => c.user.id === selectedCollaboratorId,
      );
      if (collab?.listings.some((l) => l.id === listingId)) {
        return selectedCollaboratorId;
      }
    }
    const fallback = collaborators.find((c) =>
      c.listings.some((l) => l.id === listingId),
    );
    return fallback?.user.id ?? null;
  }, [selectedCollaboratorId, listingId, collaborators]);

  const toggle = (userId: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const getLastActivity = (collaborator: CollaboratorListingsDTO) => {
    if (!collaborator.listings.length) {
      return null;
    }
    const dates = collaborator.listings
      .map((l) => (l.lastUpdatedAt ? new Date(l.lastUpdatedAt).getTime() : 0))
      .filter((t) => t > 0);
    if (!dates.length) {
      return null;
    }
    return new Date(Math.max(...dates));
  };

  const totalAssignations = collaborators.reduce(
    (acc, c) => acc + c.listings.length,
    0,
  );

  if (!collaborators.length) {
    return (
      <p className="py-4 text-xs text-center text-muted-foreground">
        {t("listing.page.collaborators.empty")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center px-1">
        <span className="text-xs font-bold tracking-wide uppercase text-majorelle">
          {t("listing.page.collaborators.title")}
        </span>
        <span className="text-xs text-muted-foreground">
          {t("listing.page.collaborators.assignations", {
            count: totalAssignations,
          })}
        </span>
      </div>

      <div className="flex flex-col gap-2 p-0 pt-0">
        {collaborators.map((collaborator) => {
          const isOpen = openIds.has(collaborator.user.id);
          const isUserSelected = location.pathname.includes(
            `/listing/team-members/${collaborator.user.id}`,
          );
          const isUserActive =
            isUserSelected ||
            effectiveSelectedCollaboratorId === collaborator.user.id;
          const lastActivity = getLastActivity(collaborator);
          const colorClass = getAvatarColor(collaborator.user.id);
          const count = collaborator.listings.length;

          return (
            <Collapsible
              key={collaborator.user.id}
              open={isOpen}
              onOpenChange={() => toggle(collaborator.user.id)}
            >
              <div
                className={cn(
                  "flex items-center w-full rounded-lg border-2 border-transparent bg-background transition-colors hover:bg-accent",
                  isUserSelected && "border-majorelle",
                )}
              >
                <NavLink
                  to={`/listing/team-members/${collaborator.user.id}`}
                  onClick={() => {
                    if (!isOpen) {
                      toggle(collaborator.user.id);
                    }
                  }}
                  className="flex flex-1 gap-2 items-center px-2 py-2 min-w-0 text-left"
                >
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarImage src={collaborator.user.imageUrl} />
                    <AvatarFallback
                      className={cn(
                        "text-xs font-semibold",
                        colorClass,
                      )}
                    >
                      {getInitialsFromParts(
                        collaborator.user.firstName,
                        collaborator.user.lastName,
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {collaborator.user.fullName}
                    </div>
                    <div className="text-xs truncate text-muted-foreground">
                      {getTeamUserRoleLabel(collaborator.user.role, t)}
                      {lastActivity && (
                        <>
                          {" · "}
                          {convertDateToDistanceNow(lastActivity, {
                            languageOverride: currentLanguage,
                          })}
                          <Tooltip>
                            <TooltipTrigger
                              asChild
                              onClick={(e) => e.preventDefault()}
                            >
                              <InfoIcon className="inline-block ml-1 size-3 shrink-0 align-[-1px]" />
                            </TooltipTrigger>
                            <TooltipContent>
                              {t(
                                "listing.page.collaborators.lastActivityTooltip",
                                {
                                  name:
                                    collaborator.user.firstName ||
                                    collaborator.user.fullName,
                                },
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  </div>
                </NavLink>
                <CollapsibleTrigger className="flex gap-1 items-center px-2 py-2 shrink-0">
                  {count === 0 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className="px-2 py-0.5 text-xs font-medium rounded-full border-transparent bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700">
                          0
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("listing.page.collaborators.assignations", {
                          count: 0,
                        })}
                      </TooltipContent>
                    </Tooltip>
                  ) : isUserActive ? (
                    <span className="flex justify-center items-center px-2 h-5 text-xs font-semibold text-white rounded-full min-w-5 bg-majorelle">
                      {count}
                    </span>
                  ) : (
                    <Badge className="px-2 py-0.5 text-xs font-medium rounded-full border-transparent bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700">
                      {count}
                    </Badge>
                  )}
                  {isOpen ? (
                    <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                  )}
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <div className="flex relative flex-col gap-2 pt-2 pl-6">
                  <span className="absolute top-2 bottom-2 left-4 w-0.5 rounded-full bg-border" />
                  {collaborator.listings.map((listing) => {
                    const isSelected =
                      listingId === listing.id &&
                      effectiveSelectedCollaboratorId === collaborator.user.id;
                    const lCount =
                      listing.listingType === "employees"
                        ? listing.employeesCount
                        : listing.companiesCount;
                    return (
                      <NavLink
                        key={listing.id}
                        to={`/listing/${listing.id}`}
                        onClick={() =>
                          setSelectedCollaboratorId(collaborator.user.id)
                        }
                        className={cn(
                          "flex justify-between items-center gap-2 rounded-lg border-2 border-transparent bg-background py-[7px] px-[7px] text-left text-sm transition-all hover:bg-accent overflow-hidden",
                          isSelected && "border-majorelle",
                        )}
                      >
                        <div className="flex flex-1 gap-2 items-center min-w-0">
                          <span className="flex justify-center items-center w-7 h-7 rounded-md shrink-0 bg-muted/70">
                            {listing.listingType === "employees" ? (
                              <UsersIcon className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <Building2Icon className="w-4 h-4 text-muted-foreground" />
                            )}
                          </span>
                          <div className="font-semibold truncate">
                            {listing.title}
                          </div>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              className={cn(
                                "px-2 py-0.5 text-xs font-medium rounded-full border-transparent shrink-0",
                                isSelected
                                  ? "bg-majorelle text-white hover:bg-majorelle"
                                  : "bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700",
                              )}
                            >
                              {lCount}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {listing.listingType === "employees"
                              ? t("listing.page.list.badges.employeesTooltip")
                              : t("listing.page.list.badges.companiesTooltip")}
                          </TooltipContent>
                        </Tooltip>
                      </NavLink>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}
